import Container from 'typedi';
import { PerformanceManager } from '../../performance/performance-manager';
import { PerformanceMarks } from '../../performance/enums/consts';
import { LocalSimpleStoreService } from '../../storage/implementations/simple-store';
import { ThemeService } from '../../theme/theme.service';
import { UrlUtils } from '../../utils/urlUtils';
import { Utils } from '../../utils';
import {
    IClientSettings,
    IKambiClientSettings,
    IPokerClientSettings,
    ISpectateClientSettings,
} from '@sparkware/uc-sdk-core';
import { WindowToken } from '../../../injection-tokens';

export abstract class ClientSettings {
    protected readonly _window: Window;
    private readonly _performanceManager: PerformanceManager;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;
    private readonly _themeService: ThemeService;
    protected readonly _urlUtils: UrlUtils;
    private readonly _utils: Utils;

    public abstract getClientSettings():
        | IKambiClientSettings
        | ISpectateClientSettings
        | IPokerClientSettings;

    protected constructor() {
        this._window = Container.get(WindowToken);
        this._performanceManager = Container.get(PerformanceManager);
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
        this._themeService = Container.get(ThemeService);
        this._urlUtils = Container.get(UrlUtils);
        this._utils = Container.get(Utils);
    }

    protected getBaseClientSettings = (): IClientSettings => {
        this.getClientSettingsPerformance();
        const { brandId, subBrandId } = this._window.pageContextManager.getBrandData();
        const { regulationTypeId, brandRegulationTypeId } =
            this._window.pageContextManager.getRegulationData();
        const { productPackage, deviceTypeId, isHybrid, isMobile } =
            this._window.pageContextManager.getDeviceData();
        const { culture, currency, clientLangIso3 } =
            this._window.pageContextManager.getLocalizationData();
        const { country, state } = this._window.pageContextManager.getGlobalizationData();
        const nativeOS = this._localSimpleStoreService.get('NativeOS');
        const nativeOSVersion = this._localSimpleStoreService.get('NativeOSVersion');
        return {
            brandID: brandId,
            country: country,
            currency: currency,
            deviceTypeID: deviceTypeId,
            isHybrid: isHybrid || this._utils.findIfIsNative(),
            isMobile: isMobile,
            languageCode: clientLangIso3,
            locale: culture,
            osName: !!nativeOS ? nativeOS : null,
            osVersion: !!nativeOSVersion ? nativeOSVersion : null,
            productPackageID: productPackage,
            regulationTypeID: regulationTypeId,
            state: state,
            subBrandID: subBrandId,
            brandRegulationTypeID: brandRegulationTypeId,
            themeMode: this._getThemeModeCode(),
            timeZoneUTCOffset: new Date().getTimezoneOffset() / 60,
            userMode: this._utils.getUserMode(),
        };
    };

    protected getClientSettingsPerformance(): void {
        this._performanceManager.startWatch(PerformanceMarks.GetClientSettingsStart);
    }

    private _getThemeModeCode = (): number => {
        const themeMode = this._themeService.getThemeModeOrDefault();
        return themeMode?.code;
    };
}
